/**
 * Les réglages des bons d'intervention : la liste des CONTRÔLES de chaque nature (« Courroies / accouplement », « T° d'huile »…),
 * dans l'ordre où le papier les imprime.
 *
 * Fonctions pures, sans dépendance au navigateur. Un contrôle a une identité stable (une clé) et un libellé : renommer ou
 * réordonner garde la clé, donc ne réécrit jamais un bon existant (un bon garde l'instantané du libellé qu'il a reçu).
 * Le serveur reste l'autorité : ces contrôles n'évitent qu'un aller-retour voué au refus, avec les mêmes règles et les mêmes
 * plafonds que `controles-nature.ts` côté API.
 */
import type { ElementControleNature, ModeleBon, NatureBon } from '../api/client';
import { nouvelleCle } from './gammes-edition';

/** Au plus ce nombre de contrôles par nature (le serveur refuse au-delà). */
export const PLAFOND_CONTROLES = 40;
/** Longueur d'un libellé de contrôle (le serveur refuse au-delà). */
export const PLAFOND_LIBELLE_CONTROLE = 120;

/** La clé de comparaison d'un libellé : sans casse, accents, espaces ni ponctuation (deux graphies = le même contrôle). */
export function cleLibelle(libelle: string): string {
  return libelle
    .normalize('NFD')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/** L'état de l'éditeur d'une nature : tout est en texte tant qu'on saisit. */
export interface NatureEditable {
  readonly label: string;
  /** Le modèle de feuille : ne change plus dès qu'un bon porte la nature (le serveur refuse alors). */
  readonly layout: ModeleBon;
  readonly recordsService: boolean;
  readonly controles: readonly ElementControleNature[];
}

export function versEditable(nature: Pick<NatureBon, 'label' | 'layout' | 'recordsService' | 'checkItems'>): NatureEditable {
  return {
    label: nature.label,
    layout: nature.layout,
    recordsService: nature.recordsService,
    controles: nature.checkItems.map((c) => ({ key: c.key, label: c.label })),
  };
}

export function estModifiee(origine: NatureEditable, courant: NatureEditable): boolean {
  return JSON.stringify(origine) !== JSON.stringify(courant);
}

/** Ajoute un contrôle en fin de liste, avec une identité neuve ; sans effet si la liste est pleine. */
export function ajouterControle(nature: NatureEditable, libelle = ''): NatureEditable {
  if (nature.controles.length >= PLAFOND_CONTROLES) return nature;
  return { ...nature, controles: [...nature.controles, { key: nouvelleCle(), label: libelle }] };
}

export function renommerControle(nature: NatureEditable, index: number, libelle: string): NatureEditable {
  return { ...nature, controles: nature.controles.map((c, i) => (i === index ? { ...c, label: libelle } : c)) };
}

export function retirerControle(nature: NatureEditable, index: number): NatureEditable {
  return { ...nature, controles: nature.controles.filter((_, i) => i !== index) };
}

export function deplacerControle(nature: NatureEditable, index: number, sens: -1 | 1): NatureEditable {
  const cible = index + sens;
  if (cible < 0 || cible >= nature.controles.length) return nature;
  const liste = [...nature.controles];
  const a = liste[index];
  const b = liste[cible];
  if (!a || !b) return nature;
  liste[index] = b;
  liste[cible] = a;
  return { ...nature, controles: liste };
}

/** Ce qui empêche d'enregistrer : le libellé de la nature et ceux des contrôles. Liste vide = enregistrable. */
export function problemesNature(nature: NatureEditable): string[] {
  const problemes: string[] = [];
  if (nature.label.trim() === '') problemes.push('Le libellé de la nature est obligatoire.');
  const vus = new Map<string, number>();
  nature.controles.forEach((c, i) => {
    const libelle = c.label.replace(/\s+/g, ' ').trim();
    if (libelle === '') {
      problemes.push(`Contrôle ${i + 1} : le libellé est obligatoire (ou retirez la ligne).`);
      return;
    }
    if (libelle.length > PLAFOND_LIBELLE_CONTROLE) {
      problemes.push(`Contrôle ${i + 1} : le libellé dépasse ${PLAFOND_LIBELLE_CONTROLE} caractères.`);
    }
    const cle = cleLibelle(libelle);
    if (cle === '') {
      problemes.push(`Contrôle ${i + 1} : « ${libelle} » ne contient ni lettre ni chiffre.`);
      return;
    }
    const premier = vus.get(cle);
    if (premier !== undefined) problemes.push(`Contrôle ${i + 1} : « ${libelle} » figure déjà (contrôle ${premier + 1}).`);
    else vus.set(cle, i);
  });
  return problemes;
}

/** La liste envoyée : libellés rognés, espaces compressés. */
export function controlesEnvoyes(nature: NatureEditable): ElementControleNature[] {
  return nature.controles.map((c) => ({ key: c.key, label: c.label.replace(/\s+/g, ' ').trim() }));
}

/** La modification envoyée : seulement ce qui a changé par rapport à la nature lue, avec le jeton de concurrence. */
export function modificationEnvoyee(
  origine: NatureEditable,
  courant: NatureEditable,
  revision: number,
): {
  revision: number;
  label?: string;
  layout?: ModeleBon;
  recordsService?: boolean;
  checkItems?: ElementControleNature[];
} {
  const controles = controlesEnvoyes(courant);
  const controlesChanges = JSON.stringify(controlesEnvoyes(origine)) !== JSON.stringify(controles);
  return {
    revision,
    ...(courant.label.trim() !== origine.label ? { label: courant.label.trim() } : {}),
    ...(courant.layout !== origine.layout ? { layout: courant.layout } : {}),
    ...(courant.recordsService !== origine.recordsService ? { recordsService: courant.recordsService } : {}),
    ...(controlesChanges ? { checkItems: controles } : {}),
  };
}

/** « 13 contrôles », « 1 contrôle », « aucun contrôle ». */
export function phraseControles(n: number): string {
  if (n === 0) return 'aucun contrôle';
  return `${n} contrôle${n > 1 ? 's' : ''}`;
}

/**
 * Le refus du serveur est-il celui du MODÈLE de feuille (« 3 bons portent déjà cette nature ») et non une révision périmée ?
 * Les deux sont des 409 : seule la phrase les distingue, et la réponse n'est pas la même (corriger ou recharger).
 */
export function refusDeModele(message: string): boolean {
  return /modèle de feuille/i.test(message);
}

/** Le rang à donner à une nature neuve : après toutes les autres (le serveur place sinon une nature neuve en tête). */
export function rangPourNouvelleNature(natures: readonly Pick<NatureBon, 'sortOrder'>[]): number {
  return natures.reduce((max, n) => Math.max(max, n.sortOrder), 0) + 10;
}
